import { useEffect, useMemo } from 'react';

import { choreographerFactory } from '@atlassian/ipm-choreographer/factory';
import { ProductIds } from '@atlassian/ipm-choreographer/constants';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getLogger } from '@confluence/logger';

import { getMeta } from './utils/getMeta.tsx';
import {
	determineAnalyticsEnvironment,
	useAnalyticsEnvironment,
} from './useAnalyticsEnvironment.ts';
import type { ChoreographerFactory } from '@atlassian/ipm-choreographer/factory/types';

let choreographerServices: ReturnType<typeof choreographerFactory>;
const ajsEnvironment = getMeta('ajs-environment');

const logger = getLogger('choreographer-services');

if (typeof globalThis.window !== 'undefined' && ajsEnvironment) {
	const environment = determineAnalyticsEnvironment(ajsEnvironment);
	choreographerServices = choreographerFactory(ProductIds.CONFLUENCE, environment);
}

export const useChoreographerServices = (): ChoreographerFactory => {
	const analyticsEnv = useAnalyticsEnvironment();
	const { cloudId, userId } = useSessionData();

	const choreographerServiceInstance = useMemo(() => {
		if (!choreographerServices && analyticsEnv) {
			choreographerServices = choreographerFactory(ProductIds.CONFLUENCE, analyticsEnv);
		}
		// Return module level choreographer services
		return choreographerServices;
	}, [analyticsEnv]);

	const { plugin } = choreographerServiceInstance ?? {};

	useEffect(() => {
		if (plugin) {
			setupChoreographerAnalyticsClient(plugin, cloudId, userId);
		}

		/**
		 * Cleanup function in case we unmount this component to remount another one.
		 */
		return () => {
			if (plugin) {
				plugin.destroy();
			}
		};
	}, [plugin, cloudId, userId]);

	return choreographerServiceInstance;
};

function setupChoreographerAnalyticsClient(
	choreographerPlugin: ReturnType<typeof choreographerFactory>['plugin'],
	tenantId: string | null,
	atlassianAccountId: string | null,
) {
	const analyticsClient = choreographerPlugin?.getAnalyticsClient();

	try {
		if (analyticsClient) {
			if (tenantId) {
				analyticsClient?.setTenantInfo('cloudId', tenantId);
			}
			if (atlassianAccountId) {
				analyticsClient?.setUserInfo('atlassianAccount', atlassianAccountId);
			}
		}
	} catch (error) {
		logger.error`Error setting up choreographer analytics client - ${error}`;
	}
}
